import { useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomTooltip } from 'src/components/CustomTooltip';
import { StaticPanel } from 'src/components/StaticPanel';
import { isNonEnglish } from 'src/data/generation';
import { getDefaultCharsByTool, isMixtureTool } from 'src/data/generationTool';
import { useIsApplicationModeInfinity } from 'src/services';
import { useBrandVoicesAssets } from 'src/store';
import { FormulaItem } from 'src/views/ProjectDocumentView/views/CanvasProject/components/FormulaItem';

import { FormulaMenu } from './views/FormulaMenu';
import { useItems } from './services';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';

const isMaxCharsDefined = (value) => Boolean(value) && value !== '0';

const errorObjectHasErrors = (errors) =>
  errors ? Object.values(errors).filter(Boolean).length > 0 : false;

const checkErrorsPresence = (maxCharsError, subGenerationToolsProps) => {
  //return true if there is an error
  if (maxCharsError) {
    return true;
  }
  if (!subGenerationToolsProps) {
    return false;
  }
  return Object.values(subGenerationToolsProps).some((props) => errorObjectHasErrors(props.errors));
};

export const BlockFormula = ({
  className,
  onChangeFormula,
  onChangeCasing,
  onChangeMaxChars,
  selectedCustomModelId,
  generating,
  generationTool,
  selectedModeId,
  selectedModeType,
  maxChars,
  casing,
  subGenerationToolsProps,
  errors,
  language,
}) => {
  const styles = useStyles();
  const [rootElement, setRootElement] = useState(null);
  const isInfinityMode = useIsApplicationModeInfinity();

  const items = useItems({
    generationTool,
    selectedModeId,
    subGenerationToolsProps,
    maxChars,
    errors,
  });
  const [persistentItems, setPersistentItems] = useState(null);
  const { brandVoicesAssets } = useBrandVoicesAssets();
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [leaveMenuOpen, setLeaveMenuOpen] = useState(false);
  const { handleFormulaClick } = useHandlers({
    setIsMenuOpened,
    setSelectedItem,
    items,
    setPersistentItems,
    setLeaveMenuOpen,
    isMenuOpened,
    selectedItem,
  });
  // there should only be one tool with an error at a time so merge all different options. Allow closing if there are no errors
  const allowCloseMenu = !checkErrorsPresence(errors?.maxChars, subGenerationToolsProps);

  const disabled = Boolean(selectedCustomModelId);

  const showItems = persistentItems ? persistentItems : items;

  const isEnglish = !isNonEnglish(language);

  const handleNullMaxChars = (
    generationTool,
    subGenerationToolsProps,
    selectedItem,
    onChangeMaxChars,
    maxChars
  ) => {
    if (isMixtureTool(generationTool)) {
      if (!isMaxCharsDefined(subGenerationToolsProps?.[selectedItem?.tool]?.maxChars)) {
        onChangeMaxChars(getDefaultCharsByTool(selectedItem?.tool), selectedItem?.tool);
      }
    } else {
      if (!isMaxCharsDefined(maxChars)) {
        onChangeMaxChars(getDefaultCharsByTool(generationTool));
      }
    }
  };

  return (
    <StaticPanel
      className={cx(styles.root, className)}
      title={isEnglish ? 'Formatting & formulas' : 'Formatting'}
      ref={setRootElement}
      // titleStonlyId={STONLY_ID.formulas} temporarily disabled until updated
    >
      <CustomTooltip title={disabled ? 'Formulas are disabled when using a custom model' : ''}>
        <div className={styles.itemsWrapper}>
          {showItems.map((item) => (
            <FormulaItem
              className={styles.formulaItem}
              classes={
                showItems.length > 1
                  ? { mainTexts: styles.alignRight }
                  : { mainTexts: styles.alignLeft }
              }
              key={item.tool}
              generationTool={item.tool}
              title={isEnglish ? item.title : null}
              onClick={() => handleFormulaClick(item)}
              disabled={generating || disabled}
              displayToolHeader={items.length > 1}
              maxChars={item.maxChars}
              isMenuOpen={isMenuOpened && selectedItem?.tool === item.tool}
            />
          ))}
        </div>
      </CustomTooltip>
      <FormulaMenu
        className={styles.blockCustomFormula}
        generationTool={generationTool}
        subGenerationTool={selectedItem?.tool}
        onClose={() => {
          if (leaveMenuOpen) {
            setLeaveMenuOpen(false);
            return;
          }
          if (allowCloseMenu) {
            handleNullMaxChars(
              generationTool,
              subGenerationToolsProps,
              selectedItem,
              onChangeMaxChars,
              maxChars
            );
            setPersistentItems(null);
            setIsMenuOpened(false);
          }
        }}
        isMenuOpen={isMenuOpened}
        referenceElement={rootElement}
        isInfinityMode={isInfinityMode}
        generationModeId={selectedModeId}
        generationModeType={selectedModeType}
        maxChars={maxChars}
        casing={casing}
        brandVoicesAssets={brandVoicesAssets}
        errors={errors}
        onChangeFormula={onChangeFormula}
        onChangeMaxChars={onChangeMaxChars}
        onChangeCasing={onChangeCasing}
        subGenerationToolsProps={subGenerationToolsProps}
        language={language}
      />
    </StaticPanel>
  );
};

BlockFormula.propTypes = {
  className: PropTypes.string,
  onChangeFormula: PropTypes.func,
  onChangeCasing: PropTypes.func,
  onChangeMaxChars: PropTypes.func,
  selectedCustomModelId: PropTypes.string,
  generating: PropTypes.bool,
  generationTool: PropTypes.string,
  selectedModeId: PropTypes.string,
  selectedModeType: PropTypes.string,
  maxChars: PropTypes.number,
  casing: PropTypes.string,
  subGenerationToolsProps: PropTypes.shape(),
  errors: PropTypes.shape(),
  language: PropTypes.string,
};
BlockFormula.defaultProps = {
  className: null,
  generating: false,
  language: null,
};
